import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Lock, Mail, Check } from "lucide-react";
import { PageWrapper } from "../components";
import { useAuth } from "../hooks/useAuth";

// Descriptive copy for each gated area
const AREA_COPY = {
  integration_program: {
    eyebrow: "The Integration Program",
    title: "A 21-day integration journey",
    description:
      "A guided experience in stillness, awareness and inner return. Each day opens gently through video, reflection and night audio — arriving here soon for your cohort.",
    accent: "#A5B4FC",
  },
  private_suite: {
    eyebrow: "Private Suite",
    title: "Your personal state-creation space",
    description:
      "Build living, personal affirmations, choose your music, and anchor your identity to an immersive audio profile. Opens once your foundation is settled in the Reset area.",
    accent: "#C7B2DE",
  },
  abundance: {
    eyebrow: "Reclaim Our Abundance",
    title: "The Living Vision Board",
    description:
      "A cinematic, audio-led immersion into the future you're already stepping into. This area is being fine-tuned — it will open as we move through the programme together.",
    accent: "#CBA153",
  },
  performance: {
    eyebrow: "Reclaim Performance",
    title: "Sport-specific state conditioning",
    description:
      "Pre-optimised audio for Golf, Tennis and Motorsport — designed to move you into the exact state the moment needs. Unlocks after your reset foundation is in place.",
    accent: "#7FB07F",
  },
  challenges: {
    eyebrow: "Reclaim Our Passion",
    title: "Daily challenges & the Hero's Journey",
    description:
      "A living, sacred practice — 21 days of challenges designed to re-ignite what already lives inside you. Arriving soon in your journey.",
    accent: "#D97757",
  },
  presence: {
    eyebrow: "Reclaim Our Presence",
    title: "21-day immersion into presence",
    description:
      "A pillar of daily practice — morning immersion, midday interrupts, evening reflection, and sleep audio. Opens once you've settled into the foundation.",
    accent: "#6EE7B7",
  },
  healing: {
    eyebrow: "Health Support",
    title: "Guided healing immersions",
    description:
      "Spinal ease, heart flow, womb warmth, brain clarity and nervous system reset — five layered audio-visual journeys. Arriving soon.",
    accent: "#E9C2CE",
  },
  default: {
    eyebrow: "Arriving Soon",
    title: "This area is still being tested",
    description:
      "We're refining this experience before it opens to your cohort. You'll receive access once it's ready — for now, return to your Reset area.",
    accent: "#D2AF82",
  },
};

export default function ComingSoon() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const areaKey = params.get("area") || "default";
  const copy = AREA_COPY[areaKey] || AREA_COPY.default;

  const [breathe, setBreathe] = useState(false);
  const [email, setEmail] = useState(user?.email || "");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setBreathe(true), 200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
    // Reset submitted when navigating between areas
    setSubmitted(false);
    setError("");
  }, [areaKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNotify = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^\S+@\S+\.\S+$/.test(trimmed)) {
      setError("Please enter a valid email");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/waitlist/notify`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: trimmed,
            area: areaKey,
            name: user?.name || null,
          }),
        }
      );
      if (!res.ok) throw new Error("signup failed");
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageWrapper>
      <div
        className="min-h-screen relative overflow-hidden"
        data-testid="coming-soon-page"
        style={{
          background:
            "radial-gradient(ellipse at 50% 30%, rgba(26,22,18,1) 0%, rgba(14,14,12,1) 70%, rgba(8,8,8,1) 100%)",
        }}
      >
        {/* Ambient glow layer */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: breathe ? 1 : 0 }}
          transition={{ duration: 2.5, ease: "easeOut" }}
          style={{
            background: `radial-gradient(ellipse at 50% 35%, ${copy.accent}18 0%, transparent 55%)`,
          }}
        />

        {/* Grain */}
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.04]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />

        {/* Back */}
        <div className="relative max-w-lg mx-auto px-6 pt-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm"
            style={{ color: "rgba(245,240,230,0.5)" }}
            data-testid="coming-soon-back-btn"
          >
            <ChevronLeft size={18} />
            <span className="font-light tracking-wide">Back</span>
          </button>
        </div>

        {/* Content */}
        <div className="relative max-w-lg mx-auto px-8 pt-16 pb-24 flex flex-col items-center text-center">
          {/* Breathing lock emblem */}
          <motion.div
            animate={{
              scale: [1, 1.06, 1],
              opacity: [0.85, 1, 0.85],
            }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            className="relative mb-12"
          >
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center"
              style={{
                background: `radial-gradient(circle, ${copy.accent}22 0%, transparent 70%)`,
                border: `1px solid ${copy.accent}33`,
              }}
            >
              <Lock size={26} strokeWidth={1.2} style={{ color: copy.accent }} />
            </div>
            <div
              className="absolute -inset-4 rounded-full blur-xl -z-10"
              style={{ background: `${copy.accent}15` }}
            />
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-[10px] uppercase tracking-[0.35em] mb-6 font-light"
            style={{ color: copy.accent, opacity: 0.7 }}
            data-testid="coming-soon-eyebrow"
          >
            {copy.eyebrow}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 1 }}
            className="text-3xl md:text-4xl leading-tight italic mb-8"
            style={{
              fontFamily: "'Playfair Display', serif",
              color: "rgba(245,240,230,0.92)",
              fontWeight: 300,
            }}
            data-testid="coming-soon-title"
          >
            {copy.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 1 }}
            className="text-base leading-relaxed font-light max-w-md"
            style={{ color: "rgba(245,240,230,0.6)" }}
            data-testid="coming-soon-description"
          >
            {copy.description}
          </motion.p>

          {/* Waitlist email capture */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 1 }}
            className="w-full max-w-sm mt-12"
          >
            {submitted ? (
              <div
                className="flex items-center justify-center gap-3 px-5 py-4 rounded-2xl"
                style={{
                  background: `${copy.accent}12`,
                  border: `1px solid ${copy.accent}33`,
                }}
                data-testid="waitlist-submitted"
              >
                <Check size={16} style={{ color: copy.accent }} />
                <p
                  className="text-sm font-light italic"
                  style={{
                    fontFamily: "'Spectral', serif",
                    color: "rgba(245,240,230,0.75)",
                  }}
                >
                  You'll hear the moment it opens.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleNotify}
                className="space-y-3"
                data-testid="waitlist-form"
              >
                <p
                  className="text-[10px] uppercase tracking-[0.3em] text-center mb-4 font-light"
                  style={{ color: "rgba(245,240,230,0.35)" }}
                >
                  Be notified when this opens
                </p>
                <div
                  className="flex items-center gap-2 rounded-full px-4 py-2.5"
                  style={{
                    background: "rgba(245,240,230,0.04)",
                    border: `1px solid ${copy.accent}22`,
                  }}
                >
                  <Mail
                    size={14}
                    style={{ color: "rgba(245,240,230,0.35)" }}
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="flex-1 bg-transparent outline-none text-sm font-light tracking-wide"
                    style={{ color: "rgba(245,240,230,0.85)" }}
                    autoComplete="email"
                    data-testid="waitlist-email-input"
                    disabled={submitting}
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-1.5 rounded-full text-xs font-light tracking-wide transition-all disabled:opacity-40"
                    style={{
                      background: `${copy.accent}22`,
                      color: copy.accent,
                      border: `1px solid ${copy.accent}44`,
                    }}
                    data-testid="waitlist-submit-btn"
                  >
                    {submitting ? "…" : "Notify me"}
                  </button>
                </div>
                {error && (
                  <p
                    className="text-xs text-center font-light"
                    style={{ color: "rgba(220,140,140,0.8)" }}
                    data-testid="waitlist-error"
                  >
                    {error}
                  </p>
                )}
              </form>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 1.2 }}
            className="mt-14 flex flex-col items-center gap-4"
          >
            <button
              onClick={() => navigate("/instant-alignment")}
              className="px-8 py-3 rounded-full text-sm font-light tracking-wide transition-all"
              style={{
                background: "rgba(245,240,230,0.06)",
                border: "1px solid rgba(245,240,230,0.12)",
                color: "rgba(245,240,230,0.85)",
              }}
              data-testid="coming-soon-reset-btn"
            >
              Return to your Reset
            </button>
            <p
              className="text-xs font-light tracking-wide"
              style={{ color: "rgba(245,240,230,0.35)" }}
            >
              Opening soon — your foundation first.
            </p>
          </motion.div>
        </div>
      </div>
    </PageWrapper>
  );
}
